import { library } from '@fortawesome/fontawesome-svg-core';
import { faFile, faFolderOpen } from '@fortawesome/free-regular-svg-icons';
import { faCircleNotch, faDownload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { observer } from 'mobx-react-lite';
import { MouseEvent, ReactElement } from 'react';
import { S_Card } from '../../../../design/card/card/S_Cards';
import { S_Checkbox } from '../../../../design/inputs/checkbox/S_Checkbox';
import { S_Link2 } from '../../../../design/links/S_Link2';
import { TrashIcon } from '../../../../design/static/icons/TrashIcon';
import { useAttachmentsPermissions } from '../../../../modules/attachments/Attachments.permissions';
import { RenameAttachmentIconedButtonContainer } from '../../../../modules/attachments/rename/ui/iconedButton/RenameAttachmentIconedButton.container';
import { GTable, IGColumnProps } from '../../../components/table/GTable';
import { EnableFor } from '../../../containers/EnableForRole';
import { toLocaleDateTimeString } from '../../../helpers/date.helpers';
import { doNothing } from '../../../helpers/function.helpers';
import { bytesToMBsOrGBs } from '../../../helpers/string.helper';
import { Home } from '../../../routes/config/Home';
import { AttachmentType, IAttachment } from '../attachments.store';
import { ICurrentDownloadAttachment } from '../attachmentUploadStore';

library.add(faFolderOpen, faFile, faDownload, faCircleNotch);

export interface IAttachmentsListTableProps {
  attachments: IAttachment[];
  currentDownloadAttachments: ICurrentDownloadAttachment[];
  isLoading: boolean;
  parentId?: string;
  sorting: {
    orderBy: string;
    orderType: string;
  };
  pagination: {
    pageNumber: number;
    pageSize: number;
    totalCount: number;
  };
  selectedAttachments: {
    ids: string[];
    isAllSelected: boolean;
  };
  workspaceId: string;
  onFolderSelect: (id: string) => void;
  onDownloadAttachments: (ids: string[]) => Promise<void>;
  onDeleted: (id: string) => void;
  onOrderingChange: (orderBy: string, orderType: string) => Promise<void>;
  onPaginationChange: (pageNumber: number, pageSize: number) => Promise<void>;
  onSelectAttachment: (id: string) => void;
  onSelectAllAttachments: () => void;
}

export const AttachmentsListTable = observer((props: IAttachmentsListTableProps): ReactElement => {
  const { t } = useTranslator();
  const permissions = useAttachmentsPermissions();

  const renderType = (attachment: IAttachment): ReactElement => <FontAwesomeIcon icon={attachment.type === AttachmentType.FOLDER ? faFolderOpen : faFile} />;

  const renderSelectHeader = (): ReactElement => (
    <S_Checkbox
      checked={props.selectedAttachments.isAllSelected}
      onClick={props.onSelectAllAttachments}
      id='id-select-all'
      value='value-select-all'
    />
  );

  const renderCheckbox = (attachment: IAttachment): ReactElement => (
    <S_Checkbox
      checked={
        props.selectedAttachments.isAllSelected ? !props.selectedAttachments.ids.includes(attachment.id) : props.selectedAttachments.ids.includes(attachment.id)
      }
      onClick={(): void => {
        props.onSelectAttachment(attachment.id);
      }}
      value={attachment.id}
      id={attachment.id}
    />
  );

  const getOnDownloadAttachments = (id: string) => (): void => {
    if (props.currentDownloadAttachments.some((a) => a.id === id)) {
      return;
    }
    void props.onDownloadAttachments([id]);
  };

  const renderName = (attachment: IAttachment): ReactElement => {
    if (attachment.type === AttachmentType.IMAGE_FILE) {
      return (
        <S_Link2
          color='text'
          className='persist-reduntant-spaces'
          title={attachment.name}
          to={getPreviewUrl(attachment.id)}
        >
          {attachment.name}
        </S_Link2>
      );
    }

    let onClick: (e: MouseEvent) => void = doNothing;

    if (attachment.type === AttachmentType.FOLDER) {
      onClick = (): void => {
        props.onFolderSelect(attachment.id);
      };
    } else {
      onClick = getOnDownloadAttachments(attachment.id);
    }

    return (
      <span
        className='persist-reduntant-spaces'
        onClick={onClick}
      >
        {attachment.name}
      </span>
    );
  };

  const getPreviewUrl = (attachmentId: string): string => {
    return Home.Attachments.List.Preview.withParams({ attachmentId, workspaceId: props.workspaceId, parentId: props.parentId || '' });
  };

  const renderCreatedDate = (attachment: IAttachment): ReactElement => <>{toLocaleDateTimeString(attachment.createdDate)}</>;

  const renderModifiedDate = (attachment: IAttachment): ReactElement => <>{toLocaleDateTimeString(attachment.modifiedDate)}</>;

  const renderAuthor = (attachment: IAttachment): ReactElement => <>{attachment.author}</>;

  const renderSize = (attachment: IAttachment): ReactElement => (
    <>
      {attachment.type === AttachmentType.FOLDER ? `${bytesToMBsOrGBs(attachment.size)} (${attachment.items} ${t('items')})` : bytesToMBsOrGBs(attachment.size)}
    </>
  );

  const renderActions = (attachment: IAttachment): ReactElement => {
    const isDownloaded = props.currentDownloadAttachments.some((a) => a.id === attachment.id);

    return (
      <>
        <RenameAttachmentIconedButtonContainer
          id={attachment.id}
          name={attachment.name}
          parentId={attachment.parentId}
        />
        <EnableFor hasPermission={permissions.canDownloadAttachments()}>
          {isDownloaded ? (
            <FontAwesomeIcon
              icon={faCircleNotch}
              className='fa-spin'
            />
          ) : (
            <IconButton
              size='sm'
              color='primary'
              onClick={(): void => {
                void props.onDownloadAttachments([attachment.id]);
              }}
              title={t('download_attachments')}
            >
              <FontAwesomeIcon icon={'download'} />
            </IconButton>
          )}
        </EnableFor>
        <EnableFor hasPermission={permissions.canDeleteAttachments()}>
          <IconButton
            size='sm'
            onClick={(): void => {
              props.onDeleted(attachment.id);
            }}
            title={t('delete_attachment')}
            disabled={isDownloaded}
          >
            <TrashIcon />
          </IconButton>
        </EnableFor>
      </>
    );
  };

  const gColumns: IGColumnProps<IAttachment>[] = [
    {
      headerName: '',
      headerRenderer: renderSelectHeader,
      renderer: renderCheckbox,
    },
    {
      headerName: t('type'),
      renderer: renderType,
    },
    {
      sortBy: 'name',
      headerName: t('name'),
      renderer: renderName,
    },
    {
      sortBy: 'createdDate',
      headerName: t('created_date'),
      renderer: renderCreatedDate,
    },
    {
      sortBy: 'modifiedDate',
      headerName: t('modified_date'),
      renderer: renderModifiedDate,
    },
    {
      sortBy: 'author',
      headerName: t('author'),
      renderer: renderAuthor,
    },
    {
      sortBy: 'size',
      headerName: t('attachment_size'),
      renderer: renderSize,
    },
    {
      headerName: t('actions'),
      renderer: renderActions,
      width: 'min-content',
      actions: true,
    },
  ];

  return (
    <S_Card>
      <GTable<IAttachment>
        isLoading={props.isLoading}
        columns={gColumns}
        items={props.attachments}
        noItemsPlaceholder={t('no_attachments')}
        paginationProps={{
          pageNumber: props.pagination.pageNumber,
          pageSize: props.pagination.pageSize,
          totalCount: props.pagination.totalCount,
          onChange: props.onPaginationChange,
        }}
        onSortChanged={props.onOrderingChange}
        sortingModel={[props.sorting]}
      />
    </S_Card>
  );
});
